/**
 * EcoWallet first-party analytics client.
 * Fires events only after cookie consent is granted.
 * Non-blocking (uses fetch keepalive), swallows all errors.
 */

const STORAGE_KEY = "ecowallet_cookie_consent";
const ANON_KEY = "ew_anon_id";
const SESSION_KEY = "ew_session_id";
const SESSION_TS_KEY = "ew_session_ts";
const SESSION_MAX_MS = 30 * 60 * 1000; // 30 min idle timeout

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** True when the user has ticked "Accept all" on the cookie banner. */
export function analyticsConsented() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.choice === "accepted";
  } catch {
    return false;
  }
}

function rid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getAnonymousId() {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = rid();
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

export function getSessionId() {
  const now = Date.now();
  const ts = Number(sessionStorage.getItem(SESSION_TS_KEY) || 0);
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid || !ts || now - ts > SESSION_MAX_MS) {
    sid = rid();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  sessionStorage.setItem(SESSION_TS_KEY, String(now));
  return sid;
}

function getUtms() {
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
      const v = p.get(k);
      if (v) out[k] = v.slice(0, 80);
    }
    return out;
  } catch {
    return {};
  }
}

function currentUserId() {
  // AuthContext stores the user object via /auth/me; we use a lightweight cache written by AuthContext.
  try {
    const cached = localStorage.getItem("ew_uid");
    return cached || null;
  } catch {
    return null;
  }
}

/**
 * Track an analytics event. No-op if consent hasn't been granted.
 * @param {string} eventName - Uppercase snake, e.g. "PRODUCT_VIEW".
 * @param {object} [props] - { product_id, path, meta, referrer, force }
 * `force: true` sends even without consent (only used for STITCH after login,
 * or when user has explicitly declined we still respect that — we don't force
 * around a decline).
 */
export function trackEvent(eventName, props = {}) {
  if (!analyticsConsented()) return;
  try {
    const body = {
      event_name: eventName,
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      user_id: currentUserId(),
      path: props.path || window.location.pathname,
      referrer: props.referrer || document.referrer || null,
      product_id: props.product_id || null,
      meta: props.meta || null,
      ...getUtms(),
    };
    const url = `${API}/analytics/track`;
    const headers = { "Content-Type": "application/json" };
    // Try sendBeacon first (fire-and-forget, survives page unload).
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    /* swallow */
  }
}

/** After signup/login, bind prior anonymous events to the user_id. */
export function stitchUser(userId) {
  if (!userId) return;
  try {
    localStorage.setItem("ew_uid", userId);
  } catch {}
  trackEvent("STITCH", { meta: { at: "login" } });
}

export function clearUser() {
  try {
    localStorage.removeItem("ew_uid");
  } catch {}
  trackEvent("LOGOUT");
}
